import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import Client from './components/Client';
import Visibility from './Visibility';

const REQUIRED_CSV_HEADERS = [
  'locationIref',
  'provider',
  'externalStoreId',
];

const EMPTY_FORM = {
  locationIref: '',
  provider: '',
  externalStoreId: '',
};

const EMPTY_FILTERS = {
  search: '',
  locationIref: '',
  provider: '',
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: '#273442',
    color: theme.palette.common.white,
    fontWeight: 700,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

export function filterLocationExternalReferences(references = [], filters = {}) {
  const keyword = String(filters.search || '').trim().toLowerCase();
  return references.filter((reference) => {
    if (
      filters.locationIref &&
      reference.locationIref !== filters.locationIref
    ) {
      return false;
    }
    if (filters.provider && reference.provider !== filters.provider) {
      return false;
    }
    if (!keyword) return true;
    return [
      reference.locationName,
      reference.locationIref,
      reference.providerLabel,
      reference.provider,
      reference.externalStoreId,
    ].some((value) => String(value || '').toLowerCase().includes(keyword));
  });
}

function countDelimiters(line) {
  let commas = 0;
  let semicolons = 0;
  let quoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"') {
      if (quoted && line[index + 1] === '"') {
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (!quoted && character === ',') {
      commas += 1;
    } else if (!quoted && character === ';') {
      semicolons += 1;
    }
  }

  return { commas, semicolons };
}

function parseDelimitedRows(text, delimiter) {
  const rows = [];
  let row = [];
  let field = '';
  let quoted = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];

    if (quoted) {
      if (character === '"') {
        if (text[index + 1] === '"') {
          field += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        field += character;
      }
      continue;
    }

    if (character === '"') {
      if (field.length > 0) {
        throw new Error('A quoted CSV value must start with a quote.');
      }
      quoted = true;
    } else if (character === delimiter) {
      row.push(field);
      field = '';
    } else if (character === '\n' || character === '\r') {
      if (character === '\r' && text[index + 1] === '\n') {
        index += 1;
      }
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += character;
    }
  }

  if (quoted) {
    throw new Error('The CSV file contains an unterminated quoted value.');
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows;
}

export function parseLocationExternalReferenceCsv(fileText) {
  const text = String(fileText || '').replace(/^\uFEFF/, '');
  const firstContentLine = text
    .split(/\r?\n/)
    .find((line) => line.trim().length > 0);

  if (!firstContentLine) {
    throw new Error('The CSV file is empty.');
  }

  const delimiterCounts = countDelimiters(firstContentLine);
  const delimiter = delimiterCounts.semicolons > delimiterCounts.commas ? ';' : ',';
  const parsedRows = parseDelimitedRows(text, delimiter).filter(
    (row) => row.some((value) => String(value).trim().length > 0)
  );
  if (!parsedRows.length) {
    throw new Error('The CSV file is empty.');
  }

  const headers = parsedRows[0].map((header) => String(header).trim());
  const normalizedHeaders = headers.map((header) => header.toLowerCase());
  const missingHeaders = REQUIRED_CSV_HEADERS.filter(
    (required) => !normalizedHeaders.includes(required.toLowerCase())
  );
  if (missingHeaders.length) {
    throw new Error(
      `CSV header must contain: ${REQUIRED_CSV_HEADERS.join(', ')}.`
    );
  }

  const headerIndexes = REQUIRED_CSV_HEADERS.reduce((result, header) => {
    result[header] = normalizedHeaders.indexOf(header.toLowerCase());
    return result;
  }, {});

  const rows = parsedRows.slice(1).map((values, index) => {
    const csvRow = index + 2;
    const result = {};
    REQUIRED_CSV_HEADERS.forEach((header) => {
      result[header] = String(values[headerIndexes[header]] || '').trim();
    });
    const missing = REQUIRED_CSV_HEADERS.filter((header) => !result[header]);
    if (missing.length) {
      throw new Error(`CSV row ${csvRow} is missing: ${missing.join(', ')}.`);
    }
    return result;
  });

  if (!rows.length) {
    throw new Error('The CSV file does not contain any data rows.');
  }
  if (rows.length > 1000) {
    throw new Error('A single CSV import can contain at most 1000 rows.');
  }

  return rows;
}

export function locationExternalReferenceError(error, fallback, reconciled = false) {
  const response = error?.responseJSON || error || {};
  if (response.detail) {
    if (Array.isArray(response.errors) && response.errors.length) {
      const details = response.errors
        .slice(0, 8)
        .map((item) => `Row ${item.row}, ${item.field}: ${item.message}`)
        .join(' ');
      return `${response.detail} ${details}`;
    }
    return response.detail;
  }

  if (response.errors && typeof response.errors === 'object') {
    const details = Object.entries(response.errors)
      .flatMap(([field, messages]) =>
        (Array.isArray(messages) ? messages : [messages]).map(
          (message) => `${field}: ${message}`
        )
      )
      .join(' ');
    if (details) return details;
  }

  if (error?.status === 403) {
    return 'You do not have permission to manage external references.';
  }
  if (
    error?.status === 0 ||
    (error?.status >= 500 && !error?.responseJSON)
  ) {
    return reconciled
      ? `${fallback} The request outcome is unknown; the authoritative list was reloaded for reconciliation.`
      : `${fallback} The request outcome is unknown and the list could not be reloaded. Restore connectivity, then refresh before retrying.`;
  }
  return fallback;
}

function downloadCsvTemplate() {
  const csv = `${REQUIRED_CSV_HEADERS.join(',')}\r\n`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'location_external_references_template.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function LocationExternalReferences({ onReady }) {
  const clientRef = useRef(null);
  if (!clientRef.current) clientRef.current = new Client('none');

  const visibilityRef = useRef(null);
  if (!visibilityRef.current) {
    visibilityRef.current = new Visibility('locations_external_references');
  }
  const scope = visibilityRef.current.scope || { visible: false, edit: false };

  const mountedRef = useRef(true);
  const readyCalledRef = useRef(false);
  const fileInputRef = useRef(null);
  const [references, setReferences] = useState([]);
  const [providers, setProviders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [notice, setNotice] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingReference, setEditingReference] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteReference, setDeleteReference] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);

  const signalReady = useCallback(() => {
    if (!readyCalledRef.current) {
      readyCalledRef.current = true;
      if (onReady) onReady();
    }
  }, [onReady]);

  const loadData = useCallback(async () => {
    if (!scope.visible) {
      setLoading(false);
      signalReady();
      return true;
    }

    setLoading(true);
    try {
      const referenceResponse =
        await clientRef.current.getLocationExternalReferences();
      if (!mountedRef.current) return;
      setReferences(referenceResponse?.externalReferences || []);
      setProviders(referenceResponse?.providers || []);
      setLocations(referenceResponse?.locations || []);
      return true;
    } catch (error) {
      if (!mountedRef.current) return;
      setNotice({
        severity: 'error',
        text: locationExternalReferenceError(
          error,
          'External references could not be loaded.'
        ),
      });
      return false;
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        signalReady();
      }
    }
  }, [scope.visible, signalReady]);

  useEffect(() => {
    mountedRef.current = true;
    loadData();
    return () => {
      mountedRef.current = false;
    };
  }, [loadData]);

  const filteredReferences = useMemo(
    () => filterLocationExternalReferences(references, filters),
    [references, filters]
  );

  const openAddDialog = () => {
    setEditingReference(null);
    setForm({
      ...EMPTY_FORM,
      provider: providers[0]?.value || '',
    });
    setFormOpen(true);
  };

  const openEditDialog = (reference) => {
    setEditingReference(reference);
    setForm({
      locationIref: reference.locationIref,
      provider: reference.provider,
      externalStoreId: reference.externalStoreId,
    });
    setFormOpen(true);
  };

  const closeFormDialog = () => {
    if (busy) return;
    setFormOpen(false);
    setEditingReference(null);
  };

  const saveReference = async () => {
    if (!form.locationIref || !form.provider || !form.externalStoreId.trim()) {
      setNotice({
        severity: 'error',
        text: 'Location, provider, and external store ID are required.',
      });
      return;
    }

    setBusy(true);
    setNotice(null);
    try {
      const payload = {
        locationIref: form.locationIref,
        provider: form.provider,
        externalStoreId: form.externalStoreId.trim(),
      };
      const result = editingReference
        ? await clientRef.current.updateLocationExternalReference(
            editingReference.iref,
            payload
          )
        : await clientRef.current.createLocationExternalReference(payload);

      if (!mountedRef.current) return;
      setFormOpen(false);
      setEditingReference(null);
      setNotice({
        severity: 'success',
        text: editingReference
          ? result?.changed === false
            ? 'The reference was already up to date.'
            : 'External reference updated.'
          : 'External reference added.',
      });
      await loadData();
    } catch (error) {
      const reconciled = await loadData();
      if (!mountedRef.current) return;
      setNotice({
        severity: 'error',
        text: locationExternalReferenceError(
          error,
          editingReference
            ? 'The reference could not be updated.'
            : 'The reference could not be added.',
          reconciled
        ),
      });
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteReference) return;
    setBusy(true);
    setNotice(null);
    try {
      await clientRef.current.deleteLocationExternalReference(
        deleteReference.iref
      );
      if (!mountedRef.current) return;
      setDeleteReference(null);
      setNotice({ severity: 'success', text: 'External reference removed.' });
      await loadData();
    } catch (error) {
      const reconciled = await loadData();
      if (!mountedRef.current) return;
      setNotice({
        severity: 'error',
        text: locationExternalReferenceError(
          error,
          'The reference could not be removed.',
          reconciled
        ),
      });
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const readCsvFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setNotice(null);
    try {
      const rows = parseLocationExternalReferenceCsv(await file.text());
      setCsvPreview({ fileName: file.name, rows });
    } catch (error) {
      setNotice({
        severity: 'error',
        text: error?.message || 'The CSV file could not be read.',
      });
    }
  };

  const importCsv = async () => {
    if (!csvPreview) return;
    setBusy(true);
    setNotice(null);
    try {
      const response = await clientRef.current.importLocationExternalReferences(
        csvPreview.rows
      );
      if (!mountedRef.current) return;
      const summary = response?.summary || {};
      setCsvPreview(null);
      setNotice({
        severity: 'success',
        text: `CSV import completed: ${summary.created || 0} created, ${
          summary.updated || 0
        } updated, ${summary.unchanged || 0} unchanged.`,
      });
      await loadData();
    } catch (error) {
      const reconciled = await loadData();
      if (!mountedRef.current) return;
      setNotice({
        severity: 'error',
        text: locationExternalReferenceError(
          error,
          'The CSV import could not be completed.',
          reconciled
        ),
      });
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  if (!scope.visible) return <div />;

  const selectedLocation =
    locations.find((location) => location.iref === form.locationIref) || null;
  const selectedFilterLocation =
    locations.find((location) => location.iref === filters.locationIref) || null;
  const hasFilters = Boolean(
    filters.search || filters.locationIref || filters.provider
  );

  return (
    <Box
      sx={{
        height: 'calc(100dvh - 74px)',
        boxSizing: 'border-box',
        overflow: 'hidden',
        bgcolor: '#eef2f6',
        p: { xs: 1.5, md: 2 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          height: '100%',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: { xs: 1.5, md: 2 }, flexShrink: 0 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#273442' }}>
                Location external IDs
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Map provider store identifiers to the location that receives their orders.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Tooltip title="Reload authoritative Backend state">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadData}
                    disabled={loading || busy}
                  >
                    Refresh
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadCsvTemplate}
                disabled={busy}
              >
                CSV template
              </Button>
              {scope.edit && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    hidden
                    onChange={readCsvFile}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={busy}
                  >
                    Import CSV
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openAddDialog}
                    disabled={busy || !providers.length || !locations.length}
                    sx={{ bgcolor: '#273442' }}
                  >
                    Add ID
                  </Button>
                </>
              )}
            </Stack>
          </Stack>

          <Alert severity="warning" sx={{ mt: 1.5, py: 0 }}>
            Changing a mapping immediately affects how new provider orders resolve to
            a location. Verify the location and case-sensitive external store ID.
          </Alert>

          {notice && (
            <Alert
              severity={notice.severity}
              onClose={() => setNotice(null)}
              sx={{ mt: 1.5, py: 0 }}
            >
              {notice.text}
            </Alert>
          )}

          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', lg: 'center' }}
            sx={{ mt: 1.5 }}
          >
            <TextField
              size="small"
              label="Search"
              placeholder="Location, provider, or external ID"
              value={filters.search}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
              sx={{ minWidth: { lg: 280 }, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Autocomplete
              size="small"
              options={locations}
              value={selectedFilterLocation}
              onChange={(_event, value) =>
                setFilters((current) => ({
                  ...current,
                  locationIref: value?.iref || '',
                }))
              }
              getOptionLabel={(option) => `${option.name} (${option.iref})`}
              isOptionEqualToValue={(option, value) => option.iref === value.iref}
              renderInput={(params) => (
                <TextField {...params} label="Location" placeholder="All locations" />
              )}
              sx={{ minWidth: { lg: 300 }, flex: 1 }}
            />
            <TextField
              select
              size="small"
              label="Provider"
              value={filters.provider}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  provider: event.target.value,
                }))
              }
              sx={{ minWidth: { lg: 190 } }}
            >
              <MenuItem value="">All providers</MenuItem>
              {providers.map((provider) => (
                <MenuItem key={provider.value} value={provider.value}>
                  {provider.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              size="small"
              startIcon={<FilterAltOffIcon />}
              onClick={() => setFilters(EMPTY_FILTERS)}
              disabled={!hasFilters}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear filters
            </Button>
            <Chip
              label={`${filteredReferences.length} / ${references.length}`}
              variant="outlined"
              size="small"
            />
          </Stack>
        </Box>

        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            borderTop: '1px solid #dce3ea',
          }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <StyledTableCell>Location</StyledTableCell>
                <StyledTableCell>Provider</StyledTableCell>
                <StyledTableCell>External store ID</StyledTableCell>
                {scope.edit && (
                  <StyledTableCell
                    align="right"
                    sx={{ position: 'sticky', right: 0, zIndex: 3 }}
                  >
                    Actions
                  </StyledTableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={scope.edit ? 4 : 3} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filteredReferences.length ? (
                filteredReferences.map((reference) => (
                  <StyledTableRow key={reference.iref} hover>
                    <StyledTableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {reference.locationName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {reference.locationIref}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        size="small"
                        label={reference.providerLabel || reference.provider}
                      />
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                    >
                      {reference.externalStoreId}
                    </StyledTableCell>
                    {scope.edit && (
                      <StyledTableCell
                        align="right"
                        sx={{
                          position: 'sticky',
                          right: 0,
                          background: 'inherit',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Tooltip title="Edit reference">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(reference)}
                              disabled={busy}
                              aria-label={`Edit ${reference.externalStoreId}`}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Remove reference">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteReference(reference)}
                              disabled={busy}
                              aria-label={`Remove ${reference.externalStoreId}`}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </StyledTableCell>
                    )}
                  </StyledTableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={scope.edit ? 4 : 3} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      {hasFilters
                        ? 'No external IDs match the current filters.'
                        : 'No external IDs are configured.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={formOpen} onClose={closeFormDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingReference ? 'Edit external reference' : 'Add external reference'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The provider and external store ID form a case-sensitive unique identity.
          </DialogContentText>
          <Stack spacing={2}>
            <Autocomplete
              options={locations}
              value={selectedLocation}
              onChange={(_event, value) =>
                setForm((current) => ({
                  ...current,
                  locationIref: value?.iref || '',
                }))
              }
              getOptionLabel={(option) => `${option.name} (${option.iref})`}
              isOptionEqualToValue={(option, value) => option.iref === value.iref}
              renderInput={(params) => (
                <TextField {...params} required label="Location" />
              )}
              disabled={busy}
            />
            <TextField
              select
              required
              label="Provider"
              value={form.provider}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  provider: event.target.value,
                }))
              }
              disabled={busy}
            >
              {providers.map((provider) => (
                <MenuItem key={provider.value} value={provider.value}>
                  {provider.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              autoFocus={!editingReference}
              label="External store ID"
              value={form.externalStoreId}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  externalStoreId: event.target.value,
                }))
              }
              inputProps={{ maxLength: 255 }}
              helperText="Leading and trailing whitespace is removed; letter case is preserved."
              disabled={busy}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFormDialog} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveReference}
            disabled={
              busy ||
              !form.locationIref ||
              !form.provider ||
              !form.externalStoreId.trim()
            }
          >
            {busy ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteReference)}
        onClose={() => !busy && setDeleteReference(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Remove external reference?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove {deleteReference?.providerLabel || deleteReference?.provider} store ID{' '}
            <strong>{deleteReference?.externalStoreId}</strong> from{' '}
            <strong>{deleteReference?.locationName}</strong>? New provider orders using
            this identity will no longer resolve until a mapping is restored.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteReference(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={busy}>
            {busy ? <CircularProgress size={20} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(csvPreview)}
        onClose={() => !busy && setCsvPreview(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Review CSV import</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Import is an atomic upsert. Existing provider/store identities will be
            reassigned to the CSV location; if any row is invalid, no rows are changed.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {csvPreview?.fileName}: {csvPreview?.rows.length || 0} row(s)
          </Typography>
          <TableContainer sx={{ border: '1px solid #dce3ea', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Location iref</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>External store ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(csvPreview?.rows || []).slice(0, 10).map((row, index) => (
                  <TableRow key={`${row.provider}-${row.externalStoreId}-${index}`}>
                    <TableCell>{row.locationIref}</TableCell>
                    <TableCell>{row.provider}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {row.externalStoreId}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {(csvPreview?.rows.length || 0) > 10 && (
            <Typography variant="caption" color="text.secondary">
              Showing the first 10 rows.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvPreview(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={importCsv} disabled={busy}>
            {busy ? <CircularProgress size={20} /> : 'Import all rows'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LocationExternalReferences;
